/**
 * SocialProof — stats bar + "trusted by" logo row. Rendered directly under
 * the hero on Home, /employers, and /talent. All content comes from
 * `/frontend/src/config/socialProof.js` so a single-file edit updates every
 * surface at once.
 */
import React from "react";
import { motion } from "framer-motion";
import { Sparkles } from "lucide-react";
import { stats, logos, copy } from "@/config/socialProof";

const Stat = ({ s, idx }) => (
    <motion.div
        initial={{ opacity: 0, y: 10 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, margin: "-40px" }}
        transition={{ duration: 0.35, delay: idx * 0.06 }}
        data-testid={`social-proof-stat-${idx}`}
        className="flex-1 min-w-[160px] px-6 py-5 first:pl-0 last:pr-0 sm:border-r sm:last:border-r-0 border-border/60"
    >
        <div className="mono-score font-heading font-bold text-3xl sm:text-4xl text-foreground tracking-tight tabular-nums">
            {s.value}
        </div>
        <div className="mt-1 text-sm font-medium text-foreground">{s.label}</div>
        {s.sublabel && (
            <div className="mt-0.5 text-[11px] text-muted-foreground">{s.sublabel}</div>
        )}
    </motion.div>
);

const LogoPill = ({ logo, idx }) => {
    if (logo.logoUrl) {
        return (
            <img
                src={logo.logoUrl}
                alt={logo.alt || logo.name}
                data-testid={`social-proof-logo-${idx}`}
                className="h-6 sm:h-7 object-contain opacity-60 hover:opacity-100 grayscale hover:grayscale-0 transition"
            />
        );
    }
    return (
        <span
            data-testid={`social-proof-logo-${idx}`}
            className="inline-flex items-center gap-1.5 rounded-md border border-dashed border-border/70 bg-card px-3 py-1.5 text-xs font-medium text-muted-foreground hover:text-foreground hover:border-emerald-500/40 transition"
        >
            <Sparkles size={11} className="text-emerald-500" />
            {logo.name}
        </span>
    );
};

export const SocialProof = ({ compact = false }) => {
    // iter 137: if both arrays are empty (post-credibility-fix default),
    // render nothing so pages don't show an empty grey band.
    if ((!stats || stats.length === 0) && (!logos || logos.length === 0)) {
        return null;
    }
    return (
        <section
            data-testid="social-proof"
            className={`${compact ? "py-8" : "py-10 sm:py-12"} border-y border-border/60 bg-muted/30`}
        >
            <div className="container-page">
                {copy.statsHeadline && (
                    <p className="text-center text-[11px] uppercase tracking-wider font-semibold text-muted-foreground">
                        {copy.statsHeadline}
                    </p>
                )}
                {stats?.length > 0 && (
                    <div className="mt-4 flex flex-wrap justify-center divide-y sm:divide-y-0 sm:flex-nowrap max-w-4xl mx-auto text-center">
                        {stats.map((s, idx) => <Stat key={s.label} s={s} idx={idx} />)}
                    </div>
                )}

                {logos?.length > 0 && (
                    <div className="mt-8 flex flex-col sm:flex-row items-center justify-center gap-x-4 gap-y-3 flex-wrap">
                        <span className="text-[11px] uppercase tracking-wider font-semibold text-muted-foreground">
                            {copy.logosHeadline}
                        </span>
                        <div className="flex items-center gap-3 flex-wrap justify-center">
                            {logos.map((l, idx) => <LogoPill key={l.name} logo={l} idx={idx} />)}
                        </div>
                    </div>
                )}
            </div>
        </section>
    );
};

export default SocialProof;
